import { Add, DeleteOutline } from '@mui/icons-material';
import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material';
import { TextField } from 'mui-rff';
import React from 'react';
import { FieldArray } from 'react-final-form-arrays';

interface TextFieldListProps {
  title: string;
  label: string;
  name: string;
  placeholder?: string;
}

export const TextFieldList = ({
  title,
  label,
  name,
  placeholder,
}: TextFieldListProps): JSX.Element => {
  return (
    <>
      <Typography variant="subtitle1" sx={{ paddingTop: 2, fontWeight: 600 }}>
        {title}
      </Typography>
      <FieldArray<string> name={name}>
        {({ fields }) => {
          const canAdd =
            !fields.value || // if undefined or null, allow add (probably no values yet)
            fields.value.length === 0 || // no values yet, allow add
            fields.value.every((value) => (value ?? '').trim() !== ''); // all values non-empty

          const addButton = (
            <Button
              variant="outlined"
              onClick={() => fields.push('')}
              startIcon={<Add />}
              sx={{ width: 'fit-content' }}
              disabled={!canAdd}
            >
              Add
            </Button>
          );

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 2 }}>
              {fields.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No entries added yet.
                </Typography>
              )}
              {fields.map((name, index) => (
                <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    name={name}
                    label={`${label} ${index + 1}`}
                    placeholder={placeholder}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    sx={{ flex: 1 }}
                  />
                  <IconButton onClick={() => fields.remove(index)}>
                    <DeleteOutline />
                  </IconButton>
                </Box>
              ))}

              <Tooltip title={!canAdd ? 'Please fill out all fields before adding another' : ''}>
                <span style={{ display: 'inline-block' }}>{addButton}</span>
              </Tooltip>
            </Box>
          );
        }}
      </FieldArray>
    </>
  );
};
